import React, { useRef } from 'react';
import time from 'utils/time';
import createMarkup from 'components/Dashboard/helpers/createMarkup';
import GeneralItemActions from 'components/Dashboard/ItemController/GeneralItemActions';
import PriorityBadge from 'components/Dashboard/ItemController/PriorityBadge';
import usePositionBodySnippet from 'components/Dashboard/hooks/usePositionBodySnippet';
import {
  ItemControllerActions,
  ListItemContainer,
  ListItemContent,
  ListItemLink,
} from 'components/Dashboard/ItemController/ListItemLayout';
import useListItemController from 'components/Dashboard/ListControl/useListItemController';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import useRememberPosition from 'components/Dashboard/hooks/useRememberPosition';

const RegulationCommentItem = ({ listRow, item: itemProp, index: indexProp, onClick }) => {
  const { contentResults } = useContentResults();
  const { Regulations } = contentResults;

  const { listContext = {}, feedOverrides = {}, display = {}, interactions = {} } = listRow ?? {};
  const { index: listIndex, item: listItem, scrollBarRef, array, zIndex } = listContext;
  const item = listItem ?? itemProp;
  const index = listIndex ?? indexProp ?? 0;
  const { date, title, contentUrl, canonicalUrl, submitter } = item ?? {};
  const { feedItemFlags: propsFeedItemFlags } = feedOverrides;
  const { hideYellowDot } = display;
  const { itemOnclick: interactionsItemOnclick, thumbDownProps, showPriorityBadge } = interactions;
  const itemOnclick = interactionsItemOnclick ?? onClick;
  const feedItemFlags = propsFeedItemFlags ?? Regulations?.feedItemFlags ?? [];
  const titleBodyComponent = useRef(null);
  const { bodySnippet } = usePositionBodySnippet({ titleBodyComponent, item: item ?? {} });
  const { unseenConditional, locationDescriptor, priorityBadgeCondition } = useListItemController({
    item: item ?? {},
    index,
    feedItemFlags: propsFeedItemFlags,
    hideYellowDot,
    thumbDownProps,
    showPriorityBadge,
  });

  const { rememberPosition } = useRememberPosition();

  if (!item) {
    return null;
  }

  const formattedDate = item.createdAtUtc
    ? time(date ?? item.postedDate, { utc: true, skipTimezone: true }).formatGeneralDate()
    : time(date ?? item.postedDate).formatGeneralDate();

  return (
    <ListItemLink
      to={locationDescriptor(contentUrl ?? canonicalUrl)}
      onClick={(e) => {
        if (scrollBarRef) {
          rememberPosition('Regulations', scrollBarRef, index);
        }
        if (itemOnclick) {
          itemOnclick(e);
        }
      }}
      style={{ zIndex: zIndex ?? (array ? array.length - index : 1) }}
    >
      <ListItemContainer className='my-2 content-item regulation-list-item' as='li' unseen={unseenConditional}>
        <ListItemContent className='px-4 py-3'>
          <div className='d-flex align-items-baseline'>
            <div className='flex-grow-1 pr-1 pr-sm-0'>
              {!!submitter && <p className='mb-1 paragraph-p1 text-main-blue'>Submitter: {submitter}</p>}
              {!!title && <h4 className='mb-2 content-item-title text-main-blue'>{title}</h4>}
              <p className='heading-date mb-0 d-flex align-items-center'>
                {!!formattedDate && <span className='date'>{formattedDate}</span>}
              </p>
            </div>
            <ItemControllerActions
              className='px-0 justify-content-end flex-centered'
              priorityBadge={
                priorityBadgeCondition ? <PriorityBadge item={item} feedItemFlags={feedItemFlags[index]} /> : null
              }
              actions={<GeneralItemActions item={item} index={index} itemActionsProps={{ noAddToReportButton: true }} />}
            />
          </div>
          {!!bodySnippet && (
            <div className='row mx-0 align-items-start legislation-title-container'>
              <div className={`legislation-title w-100 px-0`}>
                <p className='title-body mt-2 mb-0 position-relative' ref={titleBodyComponent}>
                  <span dangerouslySetInnerHTML={createMarkup(bodySnippet)} />
                </p>
              </div>
            </div>
          )}
        </ListItemContent>
      </ListItemContainer>
    </ListItemLink>
  );
};

export default RegulationCommentItem;
